import {
  Column,
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  useServerEnquiry,
} from '@access-erp/common';
import { useEffect, useRef, useState } from 'react';
import { Guid } from 'guid-typescript';

import { UpliftTypeWithId } from '@/utils/data-helpers/types';
import { useColumns } from './hooks/useColumns';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import useGetUplifts from './hooks/useGetUplifts';
import { useCancelUplifts } from './hooks/useCancelUplifts';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { GetPaginatedFilteredUpliftsVariables }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredUplifts.query';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import { useRightPanel } from './hooks/useRightPanel';
import { UserRole } from '@/config/roles.config';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { CancelUpliftsInputType, Forms } from '@/graphql/contracts/_generated_/contracts';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { User } from '@/hooks/identity/responseModels/user';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import CancelUpliftModal from './components/CancelUpliftModal/CancelUpliftModal';

const UpliftsHistoryComponent = (): React.JSX.Element => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const gridRef = useRef<EnquiryHandle<UpliftTypeWithId>>(null);
  const { headerColumns, goToUpliftsBySearch } = useColumns();
  const { searchUsers } = useSearchUsers();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  const { getUplifts } = useGetUplifts();
  const { cancelUplifts } = useCancelUplifts();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const enquiry = useServerEnquiry<
  UpliftTypeWithId,
  GetPaginatedFilteredUpliftsVariables['filter']
  >({
    gridRef,
    headerColumns: headerColumns as Column<UpliftTypeWithId>[],
    initialFilter: replaceAdvancedValueType(savedFilters),
  });
  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);
  const adminRoles = [UserRole.ADMIN, UserRole.FULL_ACCESS];


  const { tabsConfiguration, isVisibleCancelUpliftModal, closeCancelUpliftModal } = useRightPanel({
    gridRef: gridRef.current ?? undefined,
    selectedHeaderRows: enquiry.selectedHeaderRows,
    userRole: userRole as UserRole,
  });

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  const getData = async (
    params: IServerSideGetRowsParams,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.Uplifts, params);

    const upliftsData = await getUplifts({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    });

    const uniqueUploadUserIds = new Set(
      (upliftsData?.items ?? []).reduce<string[]>((ids, item) => {
        if (item?.createdBy) {
          ids.push(item.createdBy);
        }
        if (item?.updatedBy) {
          ids.push(item.updatedBy);
        }
        return ids;
      }, []),
    );

    let activeUsers: User[];
    if (uniqueUploadUserIds.size > 0) {
      activeUsers = await searchUsers({ ids: Array.from(uniqueUploadUserIds) });
    }

    const upliftsWithMatchedUsers = upliftsData?.items?.map((uplift) => {
      const createdByMatchedUser = activeUsers?.find((user) => user?.Id === uplift?.createdBy);
      const updatedByMatchedUser = activeUsers?.find((user) => user?.Id === uplift?.updatedBy);
      return {
        ...uplift,
        id: uplift?.iD ?? Guid.create().toString(),
        createdBy: createdByMatchedUser
          ? `${createdByMatchedUser.FirstName} ${createdByMatchedUser.LastName}`
          : null,
        updatedBy: updatedByMatchedUser
          ? `${updatedByMatchedUser.FirstName} ${updatedByMatchedUser.LastName}`
          : null,
      };
    });

    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToUpliftsBySearch(enquiry.serverSide?.current?.search);
    }

    return {
      items: upliftsWithMatchedUsers ?? [],
      pageInfo: {
        hasNextPage: upliftsData?.pageInfo.hasNextPage,
        hasPreviousPage: upliftsData?.pageInfo.hasPreviousPage,
        endCursor: upliftsData?.pageInfo?.endCursor,
        startCursor: upliftsData?.pageInfo?.startCursor,
      },
    };
  };

  const handleCancelConfirm = (upliftIdsAndReason: CancelUpliftsInputType, cb: () => void) => {
    cancelUplifts({ upliftIdsAndReason, onSuccess: cb });
  };

  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.Uplifts}
        formName={Forms.Uplifts}
        headerProps={{
          primaryTitle: 'Uplifts History',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing Contract Number',
          search: searchTextFromQuery,
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
        }}
        detailProps={{
          exportFileName: 'Uplifts History',
          rowGroupPanelShow: 'always',
          masterDetail: true,
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={userRole && adminRoles.includes(userRole) ? tabsConfiguration : undefined}
      />
      <CancelUpliftModal
        isVisibleCancelUpliftModal={isVisibleCancelUpliftModal}
        closeCancelUpliftModal={closeCancelUpliftModal}
        confirmCanceluplift={handleCancelConfirm}
        selectedRows={enquiry.selectedHeaderRows}
        gridRef={gridRef}
      />
    </>
  );
};

export default UpliftsHistoryComponent;
